import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, effect, ElementRef, inject, Input, input, linkedSignal, OnDestroy, signal, untracked, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Chart, registerables } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import moment from 'moment-timezone';
import { ChipModel } from '../../../models/chip.model';
import { PerformanceEvent } from '../../../models/performance-event.model';
import { ChartDataPoint } from '../../../models/performance-chart.model';
import { BaseCardComponent } from '../../shared/components/base-card/base-card.component';
import { ChipsComponent } from '../../shared/components/chips/chips.component';
import { RoutesName } from '../../shared/constants/routes-name';
import { assignColors, getColor, getHoverColor } from '../../shared/utils/performance.utils';
import { selectViewMode, selectOrganizations, selectProducts } from '../../../store/selectors/pd.selectors';
import * as PDActions from '../../../store/actions/pd.actions';
import { ProgramOrganizationType } from '../../../models/program-organization.model';
import { CHART_CONSTANTS, PerformanceChartConfig } from './performance-chart-options';
import { EmptyStateComponent } from '../../shared/components/empty-state/empty-state.component';
import { CookieService } from 'ngx-shared-services';
import { environment } from '../../../environments/environment';
import { LocaleTimeUtils } from '../../shared/utils/locale-time.utils';

// Register Chart.js components
Chart.register(...registerables, annotationPlugin);

@Component({
  selector: 'fdr-past-performance-ui-performance-chart',
  imports: [CommonModule, BaseCardComponent, ChipsComponent, TranslateModule, EmptyStateComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './performance-chart.component.html',
  styleUrl: './performance-chart.component.scss',
})
export class PerformanceChartComponent implements OnDestroy {
  @ViewChild('chartCanvas', { static: true }) chartCanvas!: ElementRef<HTMLCanvasElement>;

  @Input() set performanceData(value: PerformanceEvent[] | null | undefined) {
    this._performanceData.set(value);
  }

  readonly translateService = inject(TranslateService);
  readonly router = inject(Router);
  readonly activeRoute = inject(ActivatedRoute);
  readonly store = inject(Store);
  private readonly cookieService = inject(CookieService);
  private readonly viewMode = this.store.selectSignal(selectViewMode);
  private readonly organizations = this.store.selectSignal(selectOrganizations);
  private readonly products = this.store.selectSignal(selectProducts);
  private readonly locale = this.cookieService.getI18NLocale() || environment.defaults.locale;

  timeZone = input<string>('UTC');
  isLoading = input<boolean>(false);
  labelAccessor = input<(e: PerformanceEvent) => string>(e => e.siteName);

  private _performanceData = signal<PerformanceEvent[] | null | undefined>(null);
  private chart: Chart | null = null;
  private colors = new Map<string, string>();
  dateRange = '';

  private readonly rebuildChart = effect(() => {
    this._performanceData();
    untracked(() => this.initializeChart());
  });

  hasData = linkedSignal<boolean>(() => {
    const data = this._performanceData();
    return !!data && data.length > 0;
  });

  chipsInfo = linkedSignal<ChipModel[]>(() => {
    const performanceData = this._performanceData();
    if (!performanceData) {
      return [];
    }
    const getLabel = untracked(() => this.labelAccessor());
    const uniqueLabels = Array.from(new Set(performanceData.map(data => getLabel(data))));
    const chipColors = assignColors(uniqueLabels);

    return [
      ...uniqueLabels.map(label => ({
        color: chipColors.get(label) ?? getColor(label),
        label,
        show: true,
      })),
    ];
  });

  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy();
    }
    document.getElementById('chartjs-tooltip')?.remove();
  }

  disableChip(siteIndex: number): void {
    if (!this.chart) return;

    const dataset = this.chart.data.datasets[siteIndex];
    if (dataset) {
      dataset.hidden = !dataset.hidden;
      this.chart.update();
    }

    if (this.chipsInfo()[siteIndex]) {
      this.chipsInfo()[siteIndex].show = !this.chipsInfo()[siteIndex].show;
    }
  }

  private initializeChart(): void {
    const performanceData = this._performanceData();
    if (!performanceData || !performanceData.length) {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
      this.dateRange = '';
      document.getElementById('chartjs-tooltip')?.remove();
      return;
    }

    const processedData = this.processData(performanceData!);
    this.createChart(processedData);
  }

  private processData(data: PerformanceEvent[]): Map<string, ChartDataPoint[]> {
    const siteDataMap = new Map<string, ChartDataPoint[]>();
    // label -> timestamp, so the axis can be ordered without parsing the label back
    const dates = new Map<string, number>();
    const orgNameById = new Map(this.organizations().map(o => [o.id, o.name]));
    const productNameById = new Map(this.products().map(p => [p.id, p.name]));
    const getLabel = this.labelAccessor();

    // Group data by label (site name, product name, or portfolio name)
    data.forEach((item: PerformanceEvent) => {
      const eventDate = new Date(item.eventStartDttm);
      const dateKey = this.formatDateForXAxis(eventDate);
      const label = getLabel(item);

      dates.set(dateKey, eventDate.valueOf());

      if (!siteDataMap.has(label)) {
        siteDataMap.set(label, []);
      }

      siteDataMap.get(label)!.push({
        x: dateKey,
        y: item.performanceValue,
        enrollment: item.enrollmentValue,
        siteName: label,
        eventDate: eventDate,
        siteId: item.siteId,
        eventId: item.eventId,
        programName: item.programName,
        organizationName: orgNameById.get(item.organizationId) ?? item.organizationName,
        productName: productNameById.get(item.productId) ?? item.productName,
        performance: item.performancePercentage,
        productId: item.productId,
      });
    });

    // Calculate date range for header
    const sortedDates = Array.from(dates.keys()).sort((a, b) => dates.get(a)! - dates.get(b)!);
    if (sortedDates.length > 0) {
      const firstDate = new Date(dates.get(sortedDates[0])!);
      const lastDate = new Date(dates.get(sortedDates[sortedDates.length - 1])!);
      this.dateRange = `${this.formatDateRange(firstDate)} - ${this.formatDateRange(lastDate)}`;
    }

    this.colors = assignColors(Array.from(siteDataMap.keys()));

    return siteDataMap;
  }

  private formatDateForXAxis(date: Date): string {
    return LocaleTimeUtils.formatShortDate(date, this.locale, { timeZone: this.timeZone() });
  }

  private formatDateRange(date: Date | moment.Moment): string {
    const momentDate = moment.isMoment(date) ? date : moment(date).tz(this.timeZone());
    return LocaleTimeUtils.formatShortDate(momentDate.toDate(), this.locale, { timeZone: this.timeZone() });
  }

  private createChart(siteDataMap: Map<string, ChartDataPoint[]>): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }

    const existing = Chart.getChart(this.chartCanvas.nativeElement);
    if (existing) {
      existing.destroy();
    }

    const ctx: CanvasRenderingContext2D | null = this.chartCanvas.nativeElement.getContext('2d');
    if (!ctx) return;

    // Get all unique dates and sort them
    const allDates = new Map<string, number>();
    siteDataMap.forEach((dataPoints: ChartDataPoint[]) => {
      dataPoints.forEach((point: ChartDataPoint) => allDates.set(point.x, point.eventDate.valueOf()));
    });
    const slotsPerDate = new Map<string, number>();
    siteDataMap.forEach((dataPoints: ChartDataPoint[]) => {
      const perDate = new Map<string, number>();
      dataPoints.forEach((point: ChartDataPoint) => perDate.set(point.x, (perDate.get(point.x) ?? 0) + 1));
      perDate.forEach((count, date) => slotsPerDate.set(date, Math.max(slotsPerDate.get(date) ?? 0, count)));
    });

    const sortedDates: string[] = [];
    Array.from(allDates.keys())
      .sort((a, b) => allDates.get(a)! - allDates.get(b)!)
      .forEach(date => {
        for (let slot = 0; slot < (slotsPerDate.get(date) ?? 1); slot++) {
          sortedDates.push(date);
        }
      });

    // Create datasets for each site
    const datasets = this.createDatasets(siteDataMap, sortedDates);

    // Get chart configuration
    const config = PerformanceChartConfig.getChartConfig(
      {
        sortedDates,
        datasets,
        timeZone: this.timeZone(),
        performanceData: this._performanceData(),
        formatTooltipDate: this.formatTooltipDate.bind(this),
        formatDateForXAxis: this.formatDateForXAxis.bind(this),
        onBarClick: this.onBarClick.bind(this),
        locale: this.locale,
      },
      this.translateService
    );

    this.chart = new Chart(ctx, config);
  }

  private createDatasets(siteDataMap: Map<string, ChartDataPoint[]>, sortedDates: string[]): any[] {
    const datasets: any[] = [];

    siteDataMap.forEach((dataPoints, siteName) => {
      const color = this.colors.get(siteName) ?? getColor(siteName);

      // grouped by date so repeated dates can be handed out one slot at a time, oldest first
      const byDate = new Map<string, ChartDataPoint[]>();
      [...dataPoints]
        .sort((a, b) => a.eventDate.valueOf() - b.eventDate.valueOf())
        .forEach(point => {
          const forDate = byDate.get(point.x);
          if (forDate) {
            forDate.push(point);
          } else {
            byDate.set(point.x, [point]);
          }
        });

      let previousDate = '';
      let occurrence = 0;
      const alignedData = sortedDates.map(date => {
        occurrence = date === previousDate ? occurrence + 1 : 0;
        previousDate = date;
        const point = byDate.get(date)?.[occurrence];
        return point ? point : { x: date, y: null, siteName };
      });

      datasets.push({
        label: siteName,
        data: alignedData,
        backgroundColor: color,
        borderColor: color,
        hoverBackgroundColor: getHoverColor(color),
        borderWidth: 0,
        categoryPercentage: CHART_CONSTANTS.CATEGORY_PERCENTAGE,
        barPercentage: CHART_CONSTANTS.BAR_PERCENTAGE,
        skipNull: true,
        type: 'bar',
        parsing: {
          xAxisKey: 'x',
          yAxisKey: 'y',
        },
      });
    });

    return datasets;
  }

  private formatTooltipDate(date: Date): string {
    const momentDate = moment(date).tz(this.timeZone()).locale(this.locale);
    const dateStr = LocaleTimeUtils.formatShortDate(momentDate.toDate(), this.locale, { timeZone: this.timeZone() });
    const timezone = momentDate.format('z');

    return `${dateStr} ${timezone}`;
  }

  protected downloadChart(): void {
    if (!this.chart) return;

    const url = this.chart.toBase64Image();
    const link = document.createElement('a');
    const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
    link.download = `performance-chart_${timestamp}.png`;
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private onBarClick(data: any) {
    if (!data || !data.eventId) return;
    const isOrg = this.viewMode() === ProgramOrganizationType.Organization;
    this.store.dispatch(
      PDActions.selectPerformanceEvent({ kind: isOrg ? 'site' : 'product', eventId: data.eventId, siteId: data.siteId, productId: data.productId })
    );
    if (isOrg) {
      this.router.navigate([RoutesName.ORGANIZATION, RoutesName.EVENT_DETAILS], { queryParamsHandling: 'preserve' });
    } else {
      this.router.navigate([RoutesName.PROGRAM, RoutesName.PORTFOLIO, data.productId], { queryParamsHandling: 'preserve' });
    }
  }
}
